import React, { useState, useEffect } from 'react';
import { Shield, Server, FileCheck, Cpu, Zap, Mail, Phone, MapPin, Menu, X, ChevronDown, LogIn, LogOut, User, Users, MessageCircle, MessageSquare, Settings, TrendingUp, BookOpen } from 'lucide-react';
import { mockData } from '../mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ChatDrawer from './ChatDrawer';
import { FEATURES } from '../config/features';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Shield: Shield,
  Server: Server,
  Cpu: Cpu,
  Zap: Zap,
  FileCheck: FileCheck,
  Settings: Settings,
  Users: Users
};

const Home = () => {
  const { isAuthenticated, user, logout, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  // Check if chat should be opened from URL parameter
  useEffect(() => {
    if (searchParams.get('chat') === 'open') {
      setIsChatOpen(true);
      // Remove the query parameter from URL
      searchParams.delete('chat');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message, {
          duration: 5000,
          position: 'top-center',
        });
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit form. Please try again.';
      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-center',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="home-container">
      {/* Header */}
      <header className="dark-header">
        <div className="logo-container">
          <div className="logo-text">WestBay</div>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="dark-nav">
          <button onClick={() => scrollToSection('services')} className="dark-nav-link">Services</button>
          <button onClick={() => scrollToSection('about')} className="dark-nav-link">About</button>
          <button onClick={() => scrollToSection('poser-ai')} className="dark-nav-link">Poser AI™</button>
          {FEATURES.BLOG_ENABLED && (
            <button onClick={() => navigate('/blog')} className="dark-nav-link">
              <BookOpen size={18} />
              <span>Blog</span>
            </button>
          )}
          {/* Login/Admin buttons temporarily disabled */}
          {/* {isAdmin && (
            <button onClick={() => window.location.href = '/admin'} className="dark-nav-link admin-link">Admin</button>
          )}
          
          {isAuthenticated ? (
            <div className="nav-auth-section">
              <span className="nav-user-email">{user?.email}</span>
              <button onClick={logout} className="nav-logout-btn-clean" title="Sign out">
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <button onClick={() => window.location.href = '/login'} className="nav-login-btn-clean" title="Sign in">
              <LogIn size={18} />
            </button>
          )} */}
        </nav>

        {/* Mobile Menu Toggle */}
        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu">
          <button onClick={() => scrollToSection('services')} className="mobile-nav-link">Services</button>
          <button onClick={() => scrollToSection('about')} className="mobile-nav-link">About</button>
          <button onClick={() => { scrollToSection('poser-ai'); setMobileMenuOpen(false); }} className="mobile-nav-link">Poser AI™</button>
          {FEATURES.BLOG_ENABLED && (
            <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="mobile-nav-link">Blog</button>
          )}
          {/* Login/Admin buttons temporarily disabled */}
          {/* {isAdmin && (
            <button onClick={() => window.location.href = '/admin'} className="mobile-nav-link admin-link">Admin</button>
          )}
          
          {isAuthenticated ? (
            <>
              <div className="mobile-nav-user">{user?.email}</div>
              <button onClick={logout} className="mobile-nav-link mobile-logout">
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </>
          ) : (
            <button onClick={() => window.location.href = '/login'} className="mobile-nav-link mobile-login">
              <LogIn size={18} />
              <span>Login</span>
            </button>
          )} */}
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content hero-content-stacked">
          <div className="hero-cloud-top">
            <img src="/hero-cloud.png" alt="Cloud Computing" className="hero-cloud-image" />
          </div>
          <div className="hero-text-center">
            <h1 className="display-huge">{mockData.company.tagline}</h1>
            <p className="body-large hero-description">
              {mockData.company.description}
            </p>
            <div className="hero-cta-group hero-cta-centered">
              <button onClick={() => scrollToSection('contact')} className="btn-primary">
                Get Started
              </button>
              <button onClick={() => scrollToSection('services')} className="btn-secondary">
                Our Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-grid">
          {mockData.stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-header">
          <h2 className="display-large">What We Do</h2>
          <p className="body-medium section-subtitle">
            Security, DevOps, and AI solutions that just work—no complexity, no stress
          </p>
        </div>
        
        <div className="services-grid">
          {mockData.services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card key={service.id} className="service-card">
                <CardHeader>
                  <div className="service-icon">
                    <IconComponent size={32} />
                  </div>
                  <CardTitle className="heading-2">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="body-medium service-description">{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="body-small">{feature}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Poser AI Section - POP ART STYLE */}
      <section id="poser-ai" className="poser-ai-section">
        <div className="poser-ai-container">
          <div className="poser-ai-image">
            <img src="/poser-ai-popart-main.png" alt="Poser AI - Pop Art Style Van Gogh with Pixel Sunglasses" />
          </div>
          <div className="poser-ai-content">
            <div className="poser-ai-badges">
              <div className="poser-ai-badge">NEW</div>
              <div className="poser-ai-badge patent">Patent Pending</div>
            </div>
            <h2 className="display-large spray-paint-title">Poser AI™</h2>
            <p className="poser-ai-tagline">Chaos. Refactored.</p>
            <p className="body-large">
              Poser AI™ isn't just another chatbot—it's your AI-powered cloud architect that designs, secures, 
              and deploys your infrastructure. Powered by advanced AI, it combines decades of real-world cloud 
              experience with automated architecture design, security compliance, and Infrastructure as Code generation.
            </p>
            <div className="poser-ai-features">
              <div className="poser-ai-feature">
                <span className="feature-icon">🏗️</span>
                <div>
                  <h4>Cloud Architect</h4>
                  <p>AI designs optimal infrastructure tailored to your specific needs and scale</p>
                </div>
              </div>
              <div className="poser-ai-feature">
                <span className="feature-icon">🔒</span>
                <div>
                  <h4>Security & Compliance</h4>
                  <p>Automated compliance scanning and threat detection (SOC 2, HIPAA, PCI-DSS)</p>
                </div>
              </div>
              <div className="poser-ai-feature">
                <span className="feature-icon">🛠️</span>
                <div>
                  <h4>Infrastructure as Code</h4>
                  <p>Auto-generates Terraform and CloudFormation for your entire stack</p>
                </div>
              </div>
              <div className="poser-ai-feature">
                <span className="feature-icon">⚡</span>
                <div>
                  <h4>End-to-End Automation</h4>
                  <p>From design to deployment, fully automated cloud operations</p>
                </div>
              </div>
            </div>
            <div className="poser-ai-cta">
              <button onClick={() => setIsChatOpen(true)} className="cta-button-primary-clean">
                <img src="/poser-ai-hero.png" alt="Poser AI Van Gogh" className="button-icon-img" />
                <span>Try Poser AI™ Free</span>
              </button>
              {FEATURES.BLOG_ENABLED && (
                <button onClick={() => navigate('/blog')} className="cta-button-secondary-clean">
                  <BookOpen size={24} />
                  <span>Read Our Blog</span>
                </button>
              )}
              <p className="cta-note">2 free questions in demo mode • No signup required</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="about-content">
          <div className="about-text">
            <h2 className="display-medium">We Make Cloud Security Feel Less Scary</h2>
            <p className="body-large">
              Here's the honest truth: cloud security doesn't have to be overwhelming. We're a veteran-owned team 
              that treats your infrastructure like our own—because we know what's at stake. Think of us as your 
              cloud safety net. We handle technology consulting, Fractional CTO services, cloud security, DevOps, 
              and AI automation so you can focus on what you do best: growing your business.
            </p>
            <p className="body-medium">
              Our approach is simple: proactive protection, smart automation, and straight talk. No jargon, no 
              scares, just solid engineering. With 30+ years of combined experience spanning data centers to modern 
              cloud, we've seen it all—and we know how to keep you secure without making it complicated. Need 
              strategic technology leadership? Our Fractional CTO services give you executive-level guidance without 
              the full-time cost. We're distributed coast-to-coast, which means someone's always got your back, 
              no matter your timezone. Whether you're a startup building your first infrastructure or an enterprise 
              scaling globally, we build systems that grow with you: secure, automated, and surprisingly easy to manage.
            </p>
          </div>
          
          <div className="about-features">
            <Accordion type="single" collapsible className="about-accordion">
              <AccordionItem value="item-1">
                <AccordionTrigger className="accordion-trigger">Security Without the Stress</AccordionTrigger>
                <AccordionContent className="accordion-content">
                  We're veteran-owned, which means we're disciplined about security but relaxed about everything 
                  else. You get enterprise-grade protection without the enterprise headaches. We actually answer 
                  our phones, explain things in plain English, and treat your business like it's our own. No 
                  fear-mongering, no unnecessary complexity—just solid protection you can count on.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger className="accordion-trigger">Proactive, Not Reactive</AccordionTrigger>
                <AccordionContent className="accordion-content">
                  We catch problems before they become problems. Our DevOps approach means automated monitoring, 
                  smart alerts, and systems that practically maintain themselves. Everything we build includes 
                  observability from day one—because surprises are great for birthdays, terrible for infrastructure. 
                  We use AI-powered tools to spot issues early and automation to fix the routine stuff, so you're 
                  never caught off guard.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger className="accordion-trigger">Always On, Coast to Coast</AccordionTrigger>
                <AccordionContent className="accordion-content">
                  Being fully distributed isn't just convenient—it's strategic. With team members from coast to 
                  coast, someone's always available when you need help. No waiting for business hours. Plus, we 
                  move faster without commute times and can flex to match your schedule. Need hands-on support? 
                  We arrange onsite visits case by case. Best of both worlds: always accessible, never intrusive.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4">
                <AccordionTrigger className="accordion-trigger">Industry Know-How</AccordionTrigger>
                <AccordionContent className="accordion-content">
                  Healthcare's HIPAA requirements? Finance's strict audits? Tech startup's rapid scaling? Retail's 
                  compliance maze? We've secured them all. Every industry has unique challenges, and we've learned 
                  them so you don't have to. We handle the regulations, the integrations, and the technical complexity—
                  you just keep doing what you do best.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          <div className="contact-info">
            <h2 className="display-medium">Let's Talk</h2>
            <p className="body-large contact-intro">
              Ready to make cloud security less scary? We're here to help—no pressure, just straight answers.
            </p>
            <div className="investor-callout">
              <div className="investor-icon">💼</div>
              <div className="investor-text">
                <h3>Seeking Angel Investors</h3>
                <p>We're raising capital to scale Poser AI™ and expand our cloud security platform. Interested in being part of the future of approachable cloud security?</p>
                <button onClick={() => window.location.href = '/investors'} className="investor-cta-btn">
                  View Investment Opportunity →
                </button>
              </div>
            </div>
            
            <div className="contact-details">
              <div className="contact-item">
                <Mail className="contact-icon" size={20} />
                <span className="body-medium">{mockData.contact.email}</span>
              </div>
              <div className="contact-item">
                <Phone className="contact-icon" size={20} />
                <span className="body-medium">{mockData.contact.phone}</span>
              </div>
              <div className="contact-item">
                <MapPin className="contact-icon" size={20} />
                <div className="contact-address">
                  <span className="body-medium">{mockData.contact.address}</span>
                  <span className="body-medium">{mockData.contact.city}</span>
                  <span className="body-small contact-tagline">{mockData.contact.tagline}</span>
                </div>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="contact-form">
            <Input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
              className="form-input"
            />
            <Input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-input"
            />
            <Input
              type="text"
              name="company"
              placeholder="Company Name"
              value={formData.company}
              onChange={handleChange}
              className="form-input"
            />
            <Textarea
              name="message"
              placeholder="Tell us about your needs"
              value={formData.message}
              onChange={handleChange}
              required
              className="form-textarea"
              rows={5}
            />
            <button 
              type="submit" 
              className="btn-primary form-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-single-line">
          <div className="footer-brand">
            <div className="logo-text">WestBay</div>
          </div>
          
          <div className="footer-center">
            <span className="body-small">© 2026 Westbay Group LLC. All rights reserved.</span>
            <span className="footer-separator">•</span>
            <button onClick={() => navigate('/privacy-policy')} className="footer-link">Privacy</button>
            <span className="footer-separator">•</span>
            <button onClick={() => navigate('/terms-of-service')} className="footer-link">Terms</button>
            <span className="footer-separator">•</span>
            <span className="footer-patent body-small">
              Poser AI™ • <span className="patent-text">Patent Pending</span>
            </span>
          </div>
          
          <button 
            onClick={() => {
              if (isAuthenticated) {
                navigate('/admin');
              } else {
                navigate('/admin/login');
              }
            }} 
            className="footer-shield-icon"
            title="Admin Dashboard"
          >
            <Shield size={24} />
          </button>
        </div>
      </footer>

      {/* Floating Contact Button */}
      <button 
        className="floating-chat-button" 
        onClick={() => scrollToSection('contact')}
        aria-label="Contact Us"
      >
        <Mail size={24} />
        <span className="floating-button-text">Contact</span>
      </button>

      {/* Poser AI Drawer */}
      <ChatDrawer isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </div>
  );
};

export default Home;